// IDE/ide/help/HelpDrawer.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Switch, Button, Checkbox, Divider, Space } from "antd";
import type { HelpCategory, HelpConfig } from "./types";
import { HELP_REGISTRY } from "./registry";
import { LanguageId } from "../core/types";

interface HelpConfiguratorDrawerProps {
    language: LanguageId;
    visible: boolean;
    onClose: () => void;
    initialConfig?: HelpConfig;
    initialHelpGrouped?: boolean;
    onToggleHelpGrouped?: (val: boolean) => void;
    onSave: (cfg: HelpConfig, helpGrouped: boolean) => void;
}

const HelpConfiguratorDrawer: React.FC<HelpConfiguratorDrawerProps> = ({
    language,
    visible,
    onClose,
    initialConfig,
    initialHelpGrouped = false,
    onToggleHelpGrouped,
    onSave,
}) => {
    const ALL = useMemo<HelpCategory[]>(
        () => HELP_REGISTRY[language] || [],
        [language]
    );

    // --- État local d'édition ---
    const [helpGroupedLocal, setHelpGroupedLocal] = useState<boolean>(!!initialHelpGrouped);

    // 🆕 Par défaut: tout désélectionné (sauf si initialConfig fourni)
    const [activeCategories, setActiveCategories] = useState<string[]>(
        initialConfig?.activeCategories ?? []
    );
    const [activeSnippets, setActiveSnippets] = useState<Record<string, string[]>>(
        initialConfig?.activeSnippets ??
        Object.fromEntries(ALL.map((c) => [c.name, []]))
    );

    // 🆕 Si le langage ou la config initiale change, on resynchronise l'état
    useEffect(() => {
        setHelpGroupedLocal(!!initialHelpGrouped);
        setActiveCategories(initialConfig?.activeCategories ?? []);
        setActiveSnippets(
            initialConfig?.activeSnippets ??
            Object.fromEntries(ALL.map((c) => [c.name, []]))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, initialConfig, initialHelpGrouped, ALL.length]);

    const toggleCategory = (cat: string, checked: boolean) => {
        setActiveCategories((prev) => (checked ? [...prev, cat] : prev.filter((c) => c !== cat)));
    };

    const toggleSnippet = (cat: string, id: string, checked: boolean) => {
        setActiveSnippets((prev) => {
            const current = new Set(prev[cat] ?? []);
            if (checked) current.add(id);
            else current.delete(id);
            return { ...prev, [cat]: Array.from(current) };
        });
    };

    // 🆕 Tout cocher pour une catégorie
    const selectAllInCategory = (cat: HelpCategory) => {
        const allIds = cat.snippets.map((s) => s.id);
        setActiveCategories((prev) =>
            prev.includes(cat.name) ? prev : [...prev, cat.name]
        );
        setActiveSnippets((prev) => ({ ...prev, [cat.name]: allIds }));
    };

    // 🆕 Tout décocher pour une catégorie
    const clearAllInCategory = (cat: HelpCategory) => {
        setActiveCategories((prev) => prev.filter((c) => c !== cat.name));
        setActiveSnippets((prev) => ({ ...prev, [cat.name]: [] }));
    };

    return (
        <Drawer title={`Configurer l'aide – ${language}`} width={720} onClose={onClose} open={visible}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>Regrouper par catégories</span>
                <Switch
                    checked={helpGroupedLocal}
                    onChange={(v) => {
                        setHelpGroupedLocal(v);
                        onToggleHelpGrouped?.(v);
                    }}
                />
            </div>

            <Divider />

            {ALL.map((cat) => {
                const isCatActive = activeCategories.includes(cat.name);
                const countChecked = (activeSnippets[cat.name] ?? []).length;
                const countTotal = cat.snippets.length;

                return (
                    <div key={cat.name} style={{ marginBottom: 16 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                justifyContent: "space-between",
                            }}
                        >
                            <div>
                                <Checkbox
                                    checked={isCatActive}
                                    onChange={(e) => toggleCategory(cat.name, e.target.checked)}
                                >
                                    <span style={{ color: cat.color, fontWeight: 600 }}>
                                        {cat.name}
                                    </span>
                                </Checkbox>
                                <span style={{ marginLeft: 8, color: "#999" }}>
                                    ({countChecked}/{countTotal})
                                </span>
                            </div>

                            {/* 🆕 Actions catégorie */}
                            <Space size={6}>
                                <Button size="small" onClick={() => selectAllInCategory(cat)}>
                                    Tout cocher
                                </Button>
                                <Button size="small" onClick={() => clearAllInCategory(cat)}>
                                    Tout décocher
                                </Button>
                            </Space>
                        </div>

                        <div
                            style={{
                                marginLeft: 24,
                                marginTop: 8,
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                                gap: 8,
                            }}
                        >
                            {cat.snippets.map((s) => (
                                <Checkbox
                                    key={s.id}
                                    checked={(activeSnippets[cat.name] ?? []).includes(s.id)}
                                    onChange={(e) => toggleSnippet(cat.name, s.id, e.target.checked)}
                                    disabled={!isCatActive}
                                >
                                    {s.label}
                                </Checkbox>
                            ))}
                        </div>
                    </div>
                );
            })}

            <Divider />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button onClick={onClose}>Annuler</Button>
                <Button
                    type="primary"
                    onClick={() => {
                        const cfg: HelpConfig = { activeCategories, activeSnippets };
                        onSave(cfg, helpGroupedLocal);
                    }}
                >
                    Enregistrer
                </Button>
            </div>
        </Drawer>
    );
};

export default HelpConfiguratorDrawer;
